import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  ClipboardCheck,
  Plus,
  Search,
  Eye,
  Edit,
  Trash2,
  FileText,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SafetyWalkManagement() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [safetyWalks, setSafetyWalks] = useState([]);
  const [filteredWalks, setFilteredWalks] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const token = localStorage.getItem("token");

  // Load sites
  useEffect(() => {
    const loadSites = async () => {
      try {
        const response = await fetch(`${API}/sites`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setSites(data);
        }
      } catch (error) {
        console.error("Error loading sites:", error);
      }
    };

    loadSites();
  }, [token]);

  // Load safety walks
  useEffect(() => {
    loadSafetyWalks();
  }, [selectedSite, selectedStatus, token]);

  const loadSafetyWalks = async () => {
    setLoading(true);
    try {
      let url = `${API}/safety-walks`;
      const params = new URLSearchParams();
      
      if (selectedSite !== "all") {
        params.append("site_id", selectedSite);
      }
      if (selectedStatus !== "all") {
        params.append("status", selectedStatus);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
        
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSafetyWalks(data);
      }
    } catch (error) {
      console.error("Error loading safety walks:", error);
      toast.error("Failed to load safety walks");
    } finally {
      setLoading(false);
    }
  };

  // Filter walks by search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredWalks(safetyWalks);
    } else {
      const filtered = safetyWalks.filter(walk => 
        walk.project_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        walk.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        walk.attendees.some(att => att.name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredWalks(filtered);
    }
  }, [searchTerm, safetyWalks]);

  const handleDelete = async (walkId) => {
    try {
      const response = await fetch(`${API}/safety-walks/${walkId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Safety walk deleted successfully");
        loadSafetyWalks();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete safety walk");
      }
    } catch (error) {
      console.error("Error deleting safety walk:", error);
      toast.error("Failed to delete safety walk");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const getStatusBadge = (status) => {
    if (status === "completed") {
      return (
        <Badge className="bg-green-600">
          <ClipboardCheck className="w-3 h-3 mr-1" />
          Completed
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">
        <FileText className="w-3 h-3 mr-1" />
        Open Walk
      </Badge>
    );
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site ? site.name : "Unknown Site";
  };

  // Calculate statistics
  const totalWalks = filteredWalks.length;
  const completedWalks = filteredWalks.filter(w => w.status === "completed").length;
  const draftWalks = totalWalks - completedWalks;
  const openActions = filteredWalks.reduce((sum, walk) => 
    sum + walk.actions.filter(a => a.status === "open").length, 0
  );

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">Safety Walks</h1>
        <p className="text-sm sm:text-base text-slate-600">Conduct and manage site safety inspections</p>
      </div>

      {/* Controls */}
      <Card className="mb-4 sm:mb-6">
        <CardContent className="pt-4 sm:pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <Label htmlFor="site-filter">Filter by Site</Label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger id="site-filter">
                  <SelectValue placeholder="All Sites" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="status-filter">Filter by Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger id="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Open Walks</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="search"
                  type="text"
                  placeholder="Project, location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {user?.role !== "view_only" && (
              <div className="flex items-end">
                <Button 
                  className="w-full"
                  onClick={() => navigate("/ohs/safety-walks/new")}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Safety Walk
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-600">Total Walks</p>
                <p className="text-2xl sm:text-3xl font-bold text-slate-800">{totalWalks}</p>
              </div>
              <ClipboardCheck className="w-8 h-8 sm:w-12 sm:h-12 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-600">Completed</p>
                <p className="text-2xl sm:text-3xl font-bold text-green-600">{completedWalks}</p>
              </div>
              <ClipboardCheck className="w-8 h-8 sm:w-12 sm:h-12 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-600">Open Walks</p>
                <p className="text-2xl sm:text-3xl font-bold text-orange-600">{draftWalks}</p>
              </div>
              <FileText className="w-8 h-8 sm:w-12 sm:h-12 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-600">Open Actions</p>
                <p className="text-2xl sm:text-3xl font-bold text-red-600">{openActions}</p>
              </div>
              <AlertCircle className="w-8 h-8 sm:w-12 sm:h-12 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Safety Walks Table */}
      <Card>
        <CardHeader>
          <CardTitle>Safety Walk Records</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-slate-600">Loading safety walks...</div>
          ) : filteredWalks.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ClipboardCheck className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">No safety walks found</p>
              <p className="text-sm mt-2">Click "New Safety Walk" to create your first inspection</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Attendees</TableHead>
                    <TableHead>Actions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWalks.map((walk) => {
                    const openActionsCount = walk.actions.filter(a => a.status === "open").length;
                    return (
                      <TableRow key={walk.id}>
                        <TableCell className="font-medium">
                          {new Date(walk.date).toLocaleDateString("en-AU")}
                        </TableCell>
                        <TableCell>{walk.project_name}</TableCell>
                        <TableCell>{walk.location}</TableCell>
                        <TableCell>{getSiteName(walk.site_id)}</TableCell>
                        <TableCell>{walk.attendees.length}</TableCell>
                        <TableCell>
                          {openActionsCount > 0 ? (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
                              {openActionsCount} open
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">None</span>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(walk.status)}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/ohs/safety-walks/${walk.id}`)}
                              title="View"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {user?.role !== "view_only" && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/ohs/safety-walks/${walk.id}/edit`)}
                                  title="Edit"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setDeleteConfirm({
                                    id: walk.id,
                                    name: walk.project_name
                                  })}
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm !== null} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the safety walk for "{deleteConfirm?.name}"?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-red-600 font-medium">
              ⚠️ This action cannot be undone. All inspection data and actions will be permanently deleted.
            </p>
            <div className="flex space-x-2">
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => handleDelete(deleteConfirm.id)}
              >
                Delete
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
